import { createSelector } from 'reselect';

import getHeightsAndScaleValues from '../helpers/presentation/chartHeightsAndScale';

import getWholeMonthData from './wholeMonthData';

import { getMaxDate, getMinCalendarDate, getPreferDirects, isLoading } from '.';

import type { Direction, State } from './types';

const getLoadedMonthChartData = createSelector(
  getWholeMonthData,
  getMinCalendarDate,
  getMaxDate,
  getPreferDirects,
  (wholeMonthData, minDate: Date, maxDate: Date, preferDirect: boolean) => {
    const prices = wholeMonthData.map((data) => data.price);

    const chartData = getHeightsAndScaleValues(prices);
    const { barHeights } = chartData;
    const barsData = wholeMonthData.map((dayData, index) => {
      const available = !preferDirect || dayData.directAvailable;
      const heightProportion = barHeights[index];
      return {
        index: index + 1,
        heightProportion: heightProportion || 0,
        date: dayData.date,
        price: dayData.price ?? null,
        scaleValue: dayData.date.getDay(),
        empty: dayData.date < minDate || dayData.date > maxDate,
        outlier: heightProportion === Number.POSITIVE_INFINITY,
        selectable: available,
        showLookupIcon: Boolean(!heightProportion && available),
      };
    });

    return {
      barsData,
      scaleCoordinates: chartData.scaleCoordinates,
    };
  },
);

const getEmptyMonthChartData = () => ({
  barsData: [],
  scaleCoordinates: [],
});

const getMonthChartData = createSelector(
  (state: State) => state,
  (_: State, direction: Direction) => direction,
  isLoading,
  (state, direction, loading: boolean) => {
    if (loading) {
      return getEmptyMonthChartData();
    }
    return getLoadedMonthChartData(state, direction);
  },
);

export type MonthChartData = ReturnType<typeof getLoadedMonthChartData>;

export default getMonthChartData;
export { getLoadedMonthChartData };
